import React from 'react';
import styled from 'styled-components';

type PolicySection = {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  subsections?: Array<{
    title: string;
    paragraphs?: string[];
    bullets?: string[];
  }>;
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #ffffff 0%, #f9f5f0 100%);
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeroSection = styled.section`
  position: relative;
  background-image: url('/landing_page_packages.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  isolation: isolate;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      125deg,
      rgba(255, 255, 255, 0.92) 0%,
      rgba(255, 255, 255, 0.82) 40%,
      rgba(255, 255, 255, 0.65) 100%
    );
    z-index: -1;
  }
`;

const HeroContent = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 120px 32px 80px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 96px 20px 64px;
  }
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 999px;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f97316;
  background: rgba(251, 146, 60, 0.2);
  margin-bottom: 20px;
`;

const HeroHeading = styled.h1`
  font-size: 44px;
  line-height: 1.15;
  margin: 0 0 16px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeroDescription = styled.p`
  max-width: 680px;
  margin: 0 auto;
  font-size: 18px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const Content = styled.main`
  flex: 1;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 48px 32px 120px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 32px 20px 80px;
  }
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 48px;
`;

const MetadataCard = styled.div`
  border-radius: 16px;
  background-color: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 20px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
`;

const MetadataLabel = styled.p`
  margin: 0 0 6px;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[500]};
`;

const MetadataValue = styled.span`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MetadataLink = styled.a`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;

  &:hover {
    color: #f97316;
  }
`;

const SectionCard = styled.section`
  background: #ffffff;
  border-radius: 24px;
  padding: 32px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
  margin-bottom: 32px;

  @media (max-width: 640px) {
    padding: 24px;
  }
`;

const SectionHeading = styled.h2`
  margin: 0 0 16px;
  font-size: 26px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
`;

const SectionParagraph = styled.p`
  margin: 0 0 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 16px;
`;

const SubsectionHeading = styled.h3`
  margin: 24px 0 10px;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.grey[800]};
`;

const BulletList = styled.ul`
  margin: 0 0 16px 20px;
  padding: 0;
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.5;
`;

const HighlightCard = styled.div`
  border-radius: 18px;
  border: 1px dashed rgba(251, 146, 60, 0.5);
  padding: 20px;
  margin-top: 24px;
  background: rgba(251, 146, 60, 0.08);
  color: ${({ theme }) => theme.colors.grey[800]};
  text-align: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
`;

const sections: PolicySection[] = [
  {
    id: '1',
    title: '1. Giriş',
    paragraphs: [
      'Sezgi ("biz", "bize", "bizim"), kullanıcıların gizliliğini korumayı taahhüt eder.',
      'Bu Gizlilik Politikası, sezgi.co adresindeki web sitemizi kullandığınızda hangi verileri topladığımızı, nasıl işlediğimizi ve hangi amaçlarla kullandığımızı açıklar.',
      "Sezgi'yi kullanarak, bu Gizlilik Politikası'nda belirtilen koşulları kabul etmiş olursunuz.",
    ],
  },
  {
    id: '2',
    title: '2. Toplanan Veriler',
    subsections: [
      {
        title: '2.1 Kullanıcı Tarafından Sağlanan Veriler',
        bullets: [
          'Arama kutusuna yazılan metin sorguları (queries)',
          'İletişim formu veya e-posta ile gönderilen bilgiler (ad, e-posta vb.)',
        ],
      },
      {
        title: '2.2 Otomatik Olarak Toplanan Veriler',
        paragraphs: ['Firebase ve Google Analytics üzerinden otomatik olarak şu veriler toplanabilir:'],
        bullets: [
          'IP adresi (anonimleştirilmiş)',
          'Tarayıcı türü',
          'Cihaz bilgileri',
          'Yaklaşık konum (şehir seviyesi)',
          'Site içi gezinme hareketleri',
          'Ziyaret süreleri ve tıklamalar',
        ],
      },
      {
        title: '2.3 Üçüncü Taraf Bağlantıları',
        paragraphs: [
          'Sezgi, Amazon gibi platformlara affiliate bağlantıları içerebilir.',
          'Bu bağlantılar üzerinden yönlendirildiğiniz sitelerin gizlilik uygulamalarından biz sorumlu değiliz.',
        ],
      },
    ],
  },
  {
    id: '3',
    title: '3. Verilerin İşlenme Amaçları',
    paragraphs: [
      'Toplanan veriler şu amaçlarla kullanılabilir:',
      'Verileriniz kesinlikle satılmaz.',
    ],
    bullets: [
      'Ürün arama sonuçlarını geliştirmek',
      'Kullanıcı deneyimini iyileştirmek',
      'Hataları tespit etmek ve çözmek',
      'Performans ve kullanım istatistiklerini analiz etmek',
      'Servis güvenliğini artırmak',
      'Affiliate tıklamalarını ve yönlendirmelerini ölçmek',
    ],
  },
  {
    id: '4',
    title: '4. Çerezler ve Benzer Teknolojiler',
    paragraphs: [
      'Şu an Sezgi çerez kullanmamaktadır.',
      'Ancak ileride kullanıcı deneyimini iyileştirmek, analytics verilerini geliştirmek veya tercihleri hatırlamak için çerezler veya localStorage kullanılabilir. Bu özellikler aktif hale geldiğinde, bu politika güncellenecektir.',
    ],
  },
  {
    id: '5',
    title: '5. Verilerin Saklanma Süresi',
    paragraphs: [
      'Kullanıcı sorguları (queries) ve kullanım verileri, teknik analiz, ürün geliştirme ve istatistik amaçlarıyla süresiz olarak saklanabilir.',
      'Kullanıcılar talep ettiğinde belirli verilerin silinmesi mümkündür (Bkz. Bölüm 8).',
    ],
  },
  {
    id: '6',
    title: '6. Verilerin Paylaşımı',
    paragraphs: [
      'Kişisel veriler üçüncü kişilerle paylaşılmaz. Yalnızca aşağıdaki durumlarda paylaşım olabilir:',
    ],
    subsections: [
      {
        title: '6.1 Hizmet Sağlayıcılarla',
        bullets: ['Firebase Hosting, Functions, Storage', 'Google Analytics', 'Amazon affiliate sistemi'],
        paragraphs: ['Bu servisler veri işleme yöntemlerinde kendi gizlilik politikalarına tabidir.'],
      },
      {
        title: '6.2 Yasal Gereklilikler',
        paragraphs: [
          'Bir mahkeme kararı veya resmi talep olması halinde gerekli bilgiler paylaşılabilir.',
        ],
      },
    ],
  },
  {
    id: '7',
    title: '7. Veri Güvenliği',
    paragraphs: [
      'Verilerinizi korumak için Firebase altyapısının sunduğu güvenlik önlemlerini ve endüstri standartlarını kullanıyoruz.',
      'Ancak hiçbir sistem %100 güvenli değildir; internet üzerinden iletilen veriler tamamen risksiz değildir.',
    ],
  },
  {
    id: '8',
    title: '8. Kullanıcı Hakları',
    paragraphs: [
      'Kullanıcılar aşağıdaki haklara sahiptir:',
      'Bu haklar için bizimle iletisim@sezgi.co üzerinden iletişime geçebilirsiniz.',
    ],
    bullets: [
      'Hakkında tutulan verilere erişme',
      'Verilerin silinmesini talep etme',
      'Analiz ve ölçümleme araçlarından çıkma',
      'Gizlilik ile ilgili sorular sorma',
    ],
  },
  {
    id: '9',
    title: '9. Üçüncü Taraf Sitelerine Bağlantılar',
    paragraphs: [
      'Affiliate bağlantıları üzerinden yönlendirildiğiniz sitelerin içerik ve gizlilik politikalarından biz sorumlu değiliz.',
      'Kullanıcıların bu sitelerin kendi politikalarını incelemesi önerilir.',
    ],
  },
  {
    id: '10',
    title: '10. Gizlilik Politikasındaki Değişiklikler',
    paragraphs: [
      'Bu Gizlilik Politikası zaman zaman güncellenebilir.',
      'Güncel sürüm her zaman https://sezgi.co adresinde yayınlanacaktır.',
    ],
  },
  {
    id: '11',
    title: '11. İletişim',
    paragraphs: ['Gizlilik ile ilgili tüm sorularınız için iletisim@sezgi.co adresine e-posta gönderebilirsiniz.'],
  },
];

const metadataEntries = [
  { label: 'Son güncelleme tarihi', value: '23/11/2025' },
  { label: 'Alan adı', value: 'https://sezgi.co', href: 'https://sezgi.co' },
  { label: 'İletişim', value: 'iletisim@sezgi.co', href: 'mailto:iletisim@sezgi.co' },
];

const GizlilikPolitikasiPage: React.FC = () => {
  return (
    <PageWrapper>
      <HeroSection>
        <HeroContent>
          <HeroBadge>Gizlilik Politikası</HeroBadge>
          <HeroHeading>Sezgi'de verilerinizi nasıl koruduğumuzu keşfedin</HeroHeading>
          <HeroDescription>
            Aşağıda, sezgi.co üzerinde topladığımız verilerin kapsamını, hangi amaçlarla işlendiğini
            ve haklarınızı bulabilirsiniz. Şeffaflık ve güvenlik bizim için temel ilkedir.
          </HeroDescription>
        </HeroContent>
      </HeroSection>

      <Content>
        <MetadataGrid>
          {metadataEntries.map((entry) => (
            <MetadataCard key={entry.label}>
              <MetadataLabel>{entry.label}</MetadataLabel>
              {entry.href ? (
                <MetadataLink href={entry.href}>{entry.value}</MetadataLink>
              ) : (
                <MetadataValue>{entry.value}</MetadataValue>
              )}
            </MetadataCard>
          ))}
        </MetadataGrid>

        {sections.map((section) => (
          <SectionCard key={section.id}>
            <SectionHeading>{section.title}</SectionHeading>

            {section.paragraphs?.map((paragraph) => (
              <SectionParagraph key={paragraph}>{paragraph}</SectionParagraph>
            ))}

            {section.bullets && (
              <BulletList>
                {section.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </BulletList>
            )}

            {section.subsections?.map((subsection, index) => (
              <div key={`${section.id}-${index}`}>
                {subsection.title && <SubsectionHeading>{subsection.title}</SubsectionHeading>}
                {subsection.paragraphs?.map((paragraph) => (
                  <SectionParagraph key={paragraph}>{paragraph}</SectionParagraph>
                ))}
                {subsection.bullets && (
                  <BulletList>
                    {subsection.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </BulletList>
                )}
              </div>
            ))}

            {section.id === '8' && (
              <HighlightCard>
                Talepleriniz için: <a href="mailto:iletisim@sezgi.co">iletisim@sezgi.co</a>
              </HighlightCard>
            )}
          </SectionCard>
        ))}
      </Content>
    </PageWrapper>
  );
};

export default GizlilikPolitikasiPage;

